// @ts-nocheck
import InAppSpy from "@vendor/inapp-spy";
import { getPageGlobalProps } from "@theme/analytics.util";
import { waitForAmplitude } from "@theme/utilities";

const SUPPORTED_APP_KEYS = new Set(["facebook", "instagram"]);
const SESSION_ATTEMPT_KEY = "bodi-iab-handoff-attempted-url";
const SESSION_STAY_IN_APP_KEY = "bodi-iab-handoff-stay-in-app";
const SESSION_PENDING_CHOICE_KEY = "bodi-iab-handoff-pending-choice";
const NATIVE_DIALOG_DECLINE_DELAY_MS = 1000;

/**
 * @typedef {'android' | 'ios' | 'unknown'} Platform
 * @typedef {'facebook' | 'instagram'} SocialAppKey
 */

/**
 * @returns {boolean}
 */
function shouldSkipEnvironment() {
  if (window.self !== window.top || window.frameElement) {
    return true;
  }

  const ua = navigator.userAgent;
  if (ua.includes("Shopify Screenshot") || ua.includes("HeadlessChrome")) {
    return true;
  }

  return Boolean(window.BODi?.configs?.inAppBrowserHandoff?.disabled);
}

/**
 * @param {string} ua
 * @returns {Platform}
 */
function getPlatform(ua) {
  if (/android/i.test(ua)) {
    return "android";
  }

  if (/iphone|ipad|ipod/i.test(ua)) {
    return "ios";
  }

  return "unknown";
}

/**
 * @param {string} destinationUrl
 * @returns {string}
 */
function buildAndroidIntentUrl(destinationUrl) {
  const url = new URL(destinationUrl);
  const intentTarget = `${url.host}${url.pathname}${url.search}${url.hash}`;
  return `intent://${intentTarget}#Intent;scheme=https;end`;
}

/**
 * @param {string} destinationUrl
 * @returns {string}
 */
function buildIosFacebookSafariUrl(destinationUrl) {
  return destinationUrl.replace(/^https:\/\//i, "x-safari-https://");
}

/**
 * @param {string} destinationUrl
 * @returns {string}
 */
function buildIosInstagramExternalUrl(destinationUrl) {
  return `instagram://extbrowser/?url=${encodeURIComponent(destinationUrl)}`;
}

/**
 * @returns {{ title: string, message: string, buttonLabel: string, dismissLabel: string }}
 */
function getCopyFromScriptElement() {
  const script = document.querySelector("script[data-iab-handoff-copy]");
  return {
    title: script?.dataset.iabTitle || "Open in your browser",
    message:
      script?.dataset.iabMessage || "For the best experience, open this page in Safari or Chrome.",
    buttonLabel: script?.dataset.iabButton || "Open in browser",
    dismissLabel: script?.dataset.iabDismiss || "Continue here",
  };
}

/**
 * @param {Record<string, unknown>} properties
 */
async function trackHandoffEvent(eventName, properties) {
  try {
    await waitForAmplitude();
  } catch {
    console.info(`[InAppBrowserHandoff] - ${eventName}`, properties);
    return;
  }

  if (!window.amplitude) {
    console.info(`[InAppBrowserHandoff] - ${eventName}`, properties);
    return;
  }

  try {
    const eventProperties = {
      ...getPageGlobalProps(window.location.href),
      ...properties,
    };
    window.amplitude.track(eventName, eventProperties);
    window.amplitude.flush();
    console.info(`[InAppBrowserHandoff] - Tracked ${eventName}`, eventProperties);
  } catch (error) {
    console.warn(`[InAppBrowserHandoff] - Failed to track ${eventName}`, { error, properties });
  }
}

/**
 * @param {string} destinationUrl
 * @returns {boolean}
 */
function hasAttemptedDestination(destinationUrl) {
  try {
    return sessionStorage.getItem(SESSION_ATTEMPT_KEY) === destinationUrl;
  } catch {
    return false;
  }
}

/**
 * @param {string} destinationUrl
 */
function markAttemptedDestination(destinationUrl) {
  try {
    sessionStorage.setItem(SESSION_ATTEMPT_KEY, destinationUrl);
  } catch {
    // Storage may be blocked in some in-app browsers; redirect protection is best-effort.
  }
}

/**
 * @returns {boolean}
 */
function hasChosenStayInApp() {
  try {
    return sessionStorage.getItem(SESSION_STAY_IN_APP_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * @param {string} [reason]
 */
function setChosenStayInApp(reason) {
  try {
    sessionStorage.setItem(SESSION_STAY_IN_APP_KEY, "true");
    sessionStorage.removeItem(SESSION_PENDING_CHOICE_KEY);
  } catch {
    // Best-effort; handoff may repeat if storage is unavailable.
  }

  if (reason) {
    trackHandoffEvent("in app browser handoff stay in app", {
      reason,
    });
  }
}

/**
 * Set when an external handoff is triggered so the next in-app page view can
 * infer the user declined (e.g. canceled Instagram's system dialog) if still here.
 */
function markHandoffPendingUserChoice() {
  try {
    sessionStorage.setItem(SESSION_PENDING_CHOICE_KEY, "true");
  } catch {
    // Best-effort.
  }
}

/**
 * @returns {boolean}
 */
function hasHandoffPendingUserChoice() {
  try {
    return sessionStorage.getItem(SESSION_PENDING_CHOICE_KEY) === "true";
  } catch {
    return false;
  }
}

function clearHandoffPendingUserChoice() {
  try {
    sessionStorage.removeItem(SESSION_PENDING_CHOICE_KEY);
  } catch {
    // Best-effort.
  }
}

/**
 * After triggering an external URL, the host app may show a system dialog. If the
 * user cancels, they remain on the page — record stay-in-app for the session.
 *
 * @param {{ appKey: SocialAppKey, platform: Platform }} context
 */
function scheduleNativeDialogDeclineDetection(context) {
  let leftPage = false;

  const onPageHide = () => {
    leftPage = true;
  };

  window.addEventListener("pagehide", onPageHide, { once: true });

  window.setTimeout(() => {
    window.removeEventListener("pagehide", onPageHide);

    if (leftPage || document.visibilityState !== "visible") {
      return;
    }

    setChosenStayInApp("native dialog cancelled");
  }, NATIVE_DIALOG_DECLINE_DELAY_MS);
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

/**
 * @param {HTMLElement} overlay
 */
function dismissFallbackOverlay(overlay) {
  const previousOverflow = overlay.dataset.previousBodyOverflow ?? "";
  overlay.remove();
  document.body.style.overflow = previousOverflow;
}

/**
 * @param {{
 *   appKey: SocialAppKey,
 *   platform: Platform,
 *   destinationUrl: string,
 *   copy: ReturnType<typeof getCopyFromScriptElement>,
 *   reason: string,
 * }} options
 */
function mountFallbackOverlay(options) {
  if (document.querySelector(".bodi-iab-handoff-overlay")) {
    return;
  }

  const { appKey, platform, destinationUrl, copy, reason } = options;
  const titleId = "bodi-iab-handoff-overlay-title";
  const messageId = "bodi-iab-handoff-overlay-message";

  const overlay = document.createElement("div");
  overlay.className = "bodi-iab-handoff-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", titleId);
  overlay.setAttribute("aria-describedby", messageId);
  overlay.dataset.previousBodyOverflow = document.body.style.overflow || "";
  overlay.innerHTML = `
    <style>
      .bodi-iab-handoff-overlay {
        position: fixed;
        inset: 0;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        background: var(--bodi-black, #101010);
        color: var(--bodi-white, #ffffff);
        font-family: inherit;
      }
      .bodi-iab-handoff-overlay__inner {
        width: 100%;
        max-width: 24rem;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 24px;
        text-align: center;
      }
      .bodi-iab-handoff-overlay__title {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 700;
        line-height: 1.25;
        color: var(--bodi-white, #ffffff);
      }
      .bodi-iab-handoff-overlay__message {
        margin: 0;
        font-size: 1rem;
        line-height: 1.5;
        color: var(--bodi-gray-light, #e8eaee);
      }
      .bodi-iab-handoff-overlay__actions {
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
      }
      .bodi-iab-handoff-overlay__actions .bodi-button {
        width: 100%;
      }
      .bodi-iab-handoff-overlay__actions .bodi-button--secondary {
        border-color: var(--bodi-white, #ffffff);
        color: var(--bodi-white, #ffffff);
      }
      .bodi-iab-handoff-overlay__actions .bodi-button--secondary:hover:not(:disabled):not([aria-disabled="true"]),
      .bodi-iab-handoff-overlay__actions .bodi-button--secondary:active:not(:disabled):not([aria-disabled="true"]) {
        background-color: rgba(255, 255, 255, 0.1);
      }
    </style>
    <div class="bodi-iab-handoff-overlay__inner">
      <h2 class="bodi-iab-handoff-overlay__title" id="${titleId}">${escapeHtml(copy.title)}</h2>
      <p class="bodi-iab-handoff-overlay__message" id="${messageId}">${escapeHtml(copy.message)}</p>
      <div class="bodi-iab-handoff-overlay__actions">
        <button type="button" class="bodi-button bodi-button--primary bodi-button--large bodi-iab-handoff-overlay__open">
          <span class="bodi-button__content">${escapeHtml(copy.buttonLabel)}</span>
        </button>
        <button type="button" class="bodi-button bodi-button--secondary bodi-button--large bodi-iab-handoff-overlay__dismiss">
          <span class="bodi-button__content">${escapeHtml(copy.dismissLabel)}</span>
        </button>
      </div>
    </div>
  `;

  document.body.style.overflow = "hidden";
  document.body.appendChild(overlay);

  const openButton = overlay.querySelector(".bodi-iab-handoff-overlay__open");
  const dismissButton = overlay.querySelector(".bodi-iab-handoff-overlay__dismiss");

  openButton?.addEventListener("click", () => {
    dismissFallbackOverlay(overlay);

    const result = attemptExternalNavigation({
      appKey,
      platform,
      destinationUrl,
      isUserInitiated: true,
    });

    trackHandoffEvent("in app browser handoff open external", {
      "in app browser source": appKey,
      platform,
      outcome: result.outcome,
      reason: result.reason,
    });

    if (result.outcome === "redirect") {
      scheduleNativeDialogDeclineDetection({ appKey, platform });
    }
  });

  dismissButton?.addEventListener("click", () => {
    dismissFallbackOverlay(overlay);
    setChosenStayInApp("fallback dismissed");
  });

  trackHandoffEvent("in app browser handoff fallback shown", {
    "in app browser source": appKey,
    platform,
    reason,
  });
}

/**
 * @param {Parameters<typeof mountFallbackOverlay>[0]} options
 */
function showFallbackBanner(options) {
  if (document.body) {
    mountFallbackOverlay(options);
    return;
  }

  document.addEventListener(
    "DOMContentLoaded",
    () => {
      mountFallbackOverlay(options);
    },
    { once: true }
  );
}

/**
 * @param {{
 *   appKey: SocialAppKey,
 *   platform: Platform,
 *   destinationUrl: string,
 *   isUserInitiated?: boolean,
 * }} options
 * @returns {{ outcome: 'redirect' | 'blocked' | 'unsupported', reason: string }}
 */
function attemptExternalNavigation(options) {
  const { appKey, platform, destinationUrl, isUserInitiated = false } = options;

  if (platform === "android") {
    const intentUrl = buildAndroidIntentUrl(destinationUrl);
    markAttemptedDestination(destinationUrl);
    markHandoffPendingUserChoice();
    window.location.href = intentUrl;
    return { outcome: "redirect", reason: "android intent" };
  }

  if (platform === "ios" && appKey === "facebook") {
    const safariUrl = buildIosFacebookSafariUrl(destinationUrl);
    markAttemptedDestination(destinationUrl);
    markHandoffPendingUserChoice();
    const opened = window.open(safariUrl, "_blank");
    if (!opened && !isUserInitiated) {
      return { outcome: "blocked", reason: "ios facebook popup blocked" };
    }
    return { outcome: "redirect", reason: "ios facebook safari scheme" };
  }

  if (platform === "ios" && appKey === "instagram") {
    const externalUrl = buildIosInstagramExternalUrl(destinationUrl);
    markAttemptedDestination(destinationUrl);
    markHandoffPendingUserChoice();
    window.location.href = externalUrl;
    return { outcome: "redirect", reason: "ios instagram extbrowser" };
  }

  return { outcome: "unsupported", reason: "unsupported platform or app" };
}

/**
 * Initializes Facebook / Instagram in-app browser external handoff.
 */
export function initInAppBrowserHandoff() {
  if (shouldSkipEnvironment()) {
    return;
  }

  let detection;
  try {
    detection = InAppSpy();
  } catch (error) {
    console.warn("[InAppBrowserHandoff] - Detection unavailable", error);
    return;
  }

  const { isInApp, appKey, ua } = detection;
  const platform = getPlatform(ua || navigator.userAgent);
  const destinationUrl = window.location.href;
  const copy = getCopyFromScriptElement();

  if (!isInApp || !appKey || !SUPPORTED_APP_KEYS.has(appKey)) {
    return;
  }

  if (hasChosenStayInApp()) {
    return;
  }

  if (hasHandoffPendingUserChoice()) {
    setChosenStayInApp("continued in app after handoff");
    return;
  }

  trackHandoffEvent("in app browser detected", {
    "in app browser source": appKey,
    platform,
  });

  if (hasAttemptedDestination(destinationUrl)) {
    showFallbackBanner({
      appKey,
      platform,
      destinationUrl,
      copy,
      reason: "redirect already attempted",
    });
    return;
  }

  const navigationResult = attemptExternalNavigation({
    appKey,
    platform,
    destinationUrl,
  });

  trackHandoffEvent("in app browser handoff attempted", {
    "in app browser source": appKey,
    platform,
    outcome: navigationResult.outcome,
    reason: navigationResult.reason,
  });

  if (navigationResult.outcome === "blocked" || navigationResult.outcome === "unsupported") {
    clearHandoffPendingUserChoice();
    showFallbackBanner({
      appKey,
      platform,
      destinationUrl,
      copy,
      reason: navigationResult.reason,
    });
    return;
  }

  if (navigationResult.outcome === "redirect") {
    scheduleNativeDialogDeclineDetection({ appKey, platform });
  }
}

initInAppBrowserHandoff();
